/**
 * Step 08 — where the captions sit (spec §2.2, `captionPosition`).
 *
 * Two cards with a picture of the frame, because the answer is a place, not a
 * number. The distance under a "Lower third" caption is DERIVED from the size
 * the previous step chose (`positionPatchFor`), so the two screens can never
 * leave a 200 px caption pinned at a 130 px caption's distance.
 *
 * Per-direction default: LTR opens on Middle, RTL on Lower — the founding
 * Arabic look. Neither writes a key until the card is clicked (spec §2.3).
 */
import { useMemo, type Dispatch } from "react";
import { logoPatchForCaption, positionPatchFor, type PositionChoice } from "../../../lib/brandSetup/geometry";
import { LAYOUT_DEFAULTS, buildBaseLayout } from "../../../lib/brandSetup/payload";
import { effectiveDesign, type BrandSetupAction, type BrandSetupState } from "../../../lib/brandSetup/state";
import { ReelTag } from "../ui";

/** Both halves of the same decision — the study writes either or both. */
const POSITION_FIELDS = ["layout.caption.justify", "layout.caption.bottomPx"] as const;

const CARDS: ReadonlyArray<{ id: PositionChoice; label: string; hint: string }> = [
  { id: "middle", label: "Middle", hint: "Captions sit over the face" },
  { id: "lower", label: "Lower third", hint: "Keeps the face clear" },
];

export function CaptionPositionStep(props: {
  state: BrandSetupState;
  dispatch: Dispatch<BrandSetupAction>;
}): React.JSX.Element {
  const { state, dispatch } = props;
  const direction = state.language.direction === "rtl" ? "rtl" : "ltr";

  const base = useMemo(() => buildBaseLayout(state), [state]);
  const patch = effectiveDesign(state)?.layout?.caption;
  const justify = patch?.justify ?? base.caption?.justify;
  const current: PositionChoice = justify === "flex-end" ? "lower" : "middle";
  const fontSizePx = patch?.fontSizePx ?? base.caption?.fontSizePx ?? 165;
  const logoBottomPx = effectiveDesign(state)?.layout?.logo?.bottomPx ?? base.logo?.bottomPx ?? LAYOUT_DEFAULTS.logoBottomPx;

  const chips = state.chips.filter((c) => (POSITION_FIELDS as readonly string[]).includes(c.field));
  const fromReel = chips.filter((c) => state.accepted[c.field] !== false);

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {fromReel.length > 0 && (
        <ReelTag
          label="From your reel"
          onReset={() => fromReel.forEach((c) => dispatch({ type: "resetChip", field: c.field }))}
        />
      )}

      <div role="radiogroup" aria-label="Caption position" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
        {CARDS.map((card, i) => {
          const on = card.id === current;
          return (
            <button
              key={card.id}
              type="button"
              role="radio"
              aria-checked={on}
              {...(i === 0 ? { "data-autofocus": true } : {})}
              onClick={() => {
                const caption = positionPatchFor(direction, card.id, fontSizePx);
                // Lower third puts the caption block where the founding logo
                // sits, so the logo moves up with it — and comes back down the
                // moment the captions return to the middle.
                const logo = logoPatchForCaption({
                  lowered: card.id === "lower",
                  fontSizePx,
                  captionBottomPx: caption?.bottomPx ?? 0,
                  currentLogoBottomPx: logoBottomPx,
                });
                dispatch({
                  type: "layoutPatch",
                  patch: { caption, ...(logo ? { logo } : {}) },
                  label: `Caption position → ${card.label}`,
                });
              }}
              style={{
                display: "grid",
                gap: 10,
                justifyItems: "center",
                padding: "16px 12px 14px",
                borderRadius: 16,
                cursor: "pointer",
                fontFamily: "inherit",
                border: on ? "1.5px solid var(--ql-yellow)" : "1px solid rgba(255,255,255,.10)",
                background: on ? "rgba(255,213,0,.07)" : "rgba(255,255,255,.02)",
                transition: "all .15s var(--ql-ease)",
              }}
            >
              <Frame lowered={card.id === "lower"} on={on} />
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ql-fg)" }}>{card.label}</span>
              <span style={{ fontSize: 11.5, lineHeight: 1.4, color: "var(--ql-fg-subtle)", textAlign: "center" }}>{card.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** A 9:16 outline with two caption bars where the card would put them — the
 *  same shape the phone beside the step is showing. */
function Frame({ lowered, on }: { lowered: boolean; on: boolean }): React.JSX.Element {
  const bar = (w: number): React.CSSProperties => ({
    width: `${w}%`,
    height: 5,
    borderRadius: 2,
    background: on ? "var(--ql-yellow)" : "var(--ql-fg-faint)",
  });
  return (
    <div
      aria-hidden
      style={{
        width: 52,
        height: 92,
        borderRadius: 7,
        border: "1px solid var(--ql-border)",
        background: "rgba(255,255,255,.03)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: lowered ? "flex-end" : "center",
        gap: 4,
        padding: lowered ? "0 7px 16px" : "0 7px",
        boxSizing: "border-box",
      }}
    >
      <span style={bar(88)} />
      <span style={bar(58)} />
    </div>
  );
}
